import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  FiCheck,
  FiChevronDown,
  FiLock,
  FiSearch,
  FiUser,
  FiUsers,
} from "react-icons/fi";

import "./EmployeeSelector.css";


const normalizeText = (
  value
) =>
  String(
    value ||
    ""
  )
    .trim();


const getEmployeeId = (
  employee
) =>
  normalizeText(
    employee?._id ||
    employee?.id
  );


const getEmployeeName = (
  employee
) =>
  normalizeText(
    employee?.name ||
    employee?.employeeName
  ) ||
  "Unnamed Employee";


const getEmployeeCode = (
  employee
) =>
  normalizeText(
    employee?.employeeCode
  );


const getEmployeeDepartment = (
  employee
) =>
  normalizeText(
    employee?.department
  );


const getInitials = (
  employee
) => {

  const name =
    getEmployeeName(
      employee
    );


  return name
    .split(
      /\s+/
    )
    .filter(
      Boolean
    )
    .slice(
      0,
      2
    )
    .map(
      (
        word
      ) =>
        word
          .charAt(
            0
          )
          .toUpperCase()
    )
    .join(
      ""
    ) ||
    "E";

};


const EmployeeSelector = ({
  employees = [],
  value = "",
  onChange,
  label = "Employee",
  placeholder = "All Employees",
  disabled = false,
  locked = false,
  showAllOption = true,
}) => {

  const wrapperRef =
    useRef(
      null
    );


  const [
    open,
    setOpen,
  ] =
    useState(
      false
    );


  const [
    search,
    setSearch,
  ] =
    useState(
      ""
    );


  const safeEmployees =
    useMemo(
      () =>
        Array.isArray(
          employees
        )
          ?
          employees
          :
          [],
      [
        employees,
      ]
    );


  const selectedEmployee =
    useMemo(
      () =>
        safeEmployees
          .find(
            (
              employee
            ) =>
              getEmployeeId(
                employee
              ) ===
              normalizeText(
                value
              )
          ) ||
        null,
      [
        safeEmployees,
        value,
      ]
    );


  const filteredEmployees =
    useMemo(
      () => {

        const normalizedSearch =
          normalizeText(
            search
          )
            .toLowerCase();


        if (
          !normalizedSearch
        ) {

          return safeEmployees;

        }


        return safeEmployees
          .filter(
            (
              employee
            ) => {

              const searchable =
                [
                  getEmployeeName(
                    employee
                  ),
                  getEmployeeCode(
                    employee
                  ),
                  getEmployeeDepartment(
                    employee
                  ),
                  employee?.mobile,
                  employee?.email,
                ]
                  .filter(
                    Boolean
                  )
                  .join(
                    " "
                  )
                  .toLowerCase();


              return searchable
                .includes(
                  normalizedSearch
                );

            }
          );

      },
      [
        safeEmployees,
        search,
      ]
    );


  useEffect(
    () => {

      if (
        !open
      ) {

        return undefined;

      }


      const handleOutsideClick =
        (
          event
        ) => {

          if (
            wrapperRef.current &&
            !wrapperRef.current.contains(
              event.target
            )
          ) {

            setOpen(
              false
            );


            setSearch(
              ""
            );

          }

        };


      document.addEventListener(
        "mousedown",
        handleOutsideClick
      );


      return () => {

        document.removeEventListener(
          "mousedown",
          handleOutsideClick
        );

      };

    },
    [
      open,
    ]
  );


  useEffect(
    () => {

      if (
        disabled ||
        locked
      ) {

        setOpen(
          false
        );


        setSearch(
          ""
        );

      }

    },
    [
      disabled,
      locked,
    ]
  );


  const handleToggle =
    () => {

      if (
        disabled ||
        locked
      ) {

        return;

      }


      setOpen(
        (
          previous
        ) =>
          !previous
      );

  };


  const selectEmployee =
    (
      employeeId
    ) => {

      if (
        disabled ||
        locked
      ) {

        return;

      }


      onChange?.(
        employeeId
      );


      setOpen(
        false
      );


      setSearch(
        ""
      );

  };


  const selectedTitle =
    selectedEmployee
      ?
      getEmployeeName(
        selectedEmployee
      )
      :
      placeholder;


  const selectedMeta =
    selectedEmployee
      ?
      [
        getEmployeeCode(
          selectedEmployee
        ),
        getEmployeeDepartment(
          selectedEmployee
        ),
      ]
        .filter(
          Boolean
        )
        .join(
          " • "
        )
      :
      "";


  return (

    <div
      ref={
        wrapperRef
      }
      className={[
        "employee-selector",
        open
          ?
          "is-open"
          :
          "",
        disabled
          ?
          "is-disabled"
          :
          "",
        locked
          ?
          "is-locked"
          :
          "",
      ]
        .filter(
          Boolean
        )
        .join(
          " "
        )}
    >

      <label>
        {label}
      </label>


      <button
        type="button"
        className="employee-selector__control"
        onClick={
          handleToggle
        }
        disabled={
          disabled
        }
        aria-expanded={
          open
        }
      >

        <span
          className={[
            "employee-selector__avatar",
            selectedEmployee
              ?
              "has-employee"
              :
              "",
          ]
            .filter(
              Boolean
            )
            .join(
              " "
            )}
        >

          {
            selectedEmployee
              ?
              getInitials(
                selectedEmployee
              )
              :
              <FiUsers />
          }

        </span>


        <span className="employee-selector__selected">

          <strong>
            {
              selectedTitle
            }
          </strong>

          {
            selectedMeta
              ?
              (
                <small>
                  {
                    selectedMeta
                  }
                </small>
              )
              :
              null
          }

        </span>


        <span className="employee-selector__end-icon">

          {
            locked
              ?
              <FiLock />
              :
              <FiChevronDown />
          }

        </span>

      </button>


      {
        open &&
        !disabled &&
        !locked
          ?
          (
            <div className="employee-selector__menu">

              <div className="employee-selector__search">

                <FiSearch />

                <input
                  type="text"
                  value={
                    search
                  }
                  onChange={
                    (
                      event
                    ) =>
                      setSearch(
                        event
                          .target
                          .value
                      )
                  }
                  placeholder="Search employee..."
                  autoFocus
                />

              </div>


              <div className="employee-selector__options">

                {
                  showAllOption
                    ?
                    (
                      <button
                        type="button"
                        className={[
                          "employee-selector__option",
                          !value
                            ?
                            "is-selected"
                            :
                            "",
                        ]
                          .filter(
                            Boolean
                          )
                          .join(
                            " "
                          )}
                        onClick={() =>
                          selectEmployee(
                            ""
                          )
                        }
                      >

                        <span className="employee-selector__option-avatar all">
                          <FiUsers />
                        </span>

                        <span className="employee-selector__option-copy">
                          <strong>
                            All Employees
                          </strong>

                          <small>
                            View tenant attendance
                          </small>
                        </span>

                        {
                          !value
                            ?
                            (
                              <FiCheck className="employee-selector__check" />
                            )
                            :
                            null
                        }

                      </button>
                    )
                    :
                    null
                }


                {
                  filteredEmployees
                    .map(
                      (
                        employee
                      ) => {

                        const employeeId =
                          getEmployeeId(
                            employee
                          );


                        const selected =
                          normalizeText(
                            value
                          ) ===
                          employeeId;


                        return (

                          <button
                            key={
                              employeeId
                            }
                            type="button"
                            className={[
                              "employee-selector__option",
                              selected
                                ?
                                "is-selected"
                                :
                                "",
                            ]
                              .filter(
                                Boolean
                              )
                              .join(
                                " "
                              )}
                            onClick={() =>
                              selectEmployee(
                                employeeId
                              )
                            }
                          >

                            <span className="employee-selector__option-avatar">
                              {
                                getInitials(
                                  employee
                                )
                              }
                            </span>

                            <span className="employee-selector__option-copy">

                              <strong>
                                {
                                  getEmployeeName(
                                    employee
                                  )
                                }
                              </strong>

                              <small>
                                {
                                  [
                                    getEmployeeCode(
                                      employee
                                    ),
                                    getEmployeeDepartment(
                                      employee
                                    ),
                                  ]
                                    .filter(
                                      Boolean
                                    )
                                    .join(
                                      " • "
                                    ) ||
                                  employee?.mobile ||
                                  "Employee"
                                }
                              </small>

                            </span>

                            {
                              selected
                                ?
                                (
                                  <FiCheck className="employee-selector__check" />
                                )
                                :
                                null
                            }

                          </button>

                        );

                      }
                    )
                }


                {
                  filteredEmployees.length ===
                    0
                    ?
                    (
                      <div className="employee-selector__empty">
                        <FiUser />

                        <span>
                          No employees found
                        </span>
                      </div>
                    )
                    :
                    null
                }

              </div>

            </div>
          )
          :
          null
      }

    </div>

  );

};


export default EmployeeSelector;
